import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import {formatForecastModelTitle} from '../../utils/Forecast/title';
import {NormalizedForecastModel} from '../../utils/Forecast/types';

/**
 * Why the forecast model shown for a station or location is the one it is.
 *
 * `resolveSelectedForecastModel` answers *which* model; this answers *why*, and
 * the two are separate because the picker has to say something true about where
 * the choice applies. The four states are different sentences, not different
 * phrasings of one:
 *
 * - `freeTier`    — the account is not premium, so `resolveSelectedForecastModel`
 *                   serves GFS regardless of anything saved. Nothing the user
 *                   chose is in effect, so no other state may claim otherwise.
 * - `overridden`  — the user picked this model, here, and it applies here only.
 * - `pinWins`     — a preferred model reaches this subject and won the walk.
 * - `fallback`    — a preference exists, but no pin reaches here, so the
 *                   "everywhere else" choice decided.
 * - `noPreference`— the profile is pristine. 27.5k accounts are in this state.
 *
 * `fallback` and `noPreference` resolve to the same model today (GFS), which is
 * exactly why they are distinguished here rather than inferred downstream: a
 * consumer comparing models cannot tell them apart, and would tell a user with
 * no preference that their preference did not reach.
 *
 * Kept platform-free per the cross-app seam: it takes plain data and returns a
 * decision plus a message descriptor. Each app renders it.
 */

/** Which of the two subject vocabularies to use. */
export type ForecastSubjectKind = 'station' | 'location';

/**
 * What every state carries in addition to the reason.
 *
 * `preferred` is what the *profile preference alone* resolves to here — the
 * same walk with the per-subject override taken out. It is deliberately not
 * `model`: when the subject is overridden, `model` is the override and
 * `preferred` is what clearing it would give you. The picker's "use my
 * preferred models" row names the second, or it offers to reset a thing by
 * naming the very thing it would reset away from.
 */
type ForecastSelectionCommon = {
  preferred: NormalizedForecastModel | undefined;
};

export type ForecastSelectionState = ForecastSelectionCommon &
  (
    | {kind: 'freeTier'; model: NormalizedForecastModel | undefined}
    | {kind: 'overridden'; model: NormalizedForecastModel | undefined}
    | {kind: 'pinWins'; model: NormalizedForecastModel | undefined; pin: string}
    | {kind: 'fallback'; model: NormalizedForecastModel | undefined}
    | {kind: 'noPreference'; model: NormalizedForecastModel | undefined}
  );

type Input = {
  /** What `resolveSelectedForecastModel` returned for this subject. */
  resolved: NormalizedForecastModel | undefined;
  /**
   * The same resolve run with an empty override dictionary — what the profile
   * preference alone gives here. Equal to `resolved` for every state but
   * `overridden`; the caller computes it because only the store holds the
   * model list and the saved order.
   */
  preferred: NormalizedForecastModel | undefined;
  /** Whether this subject carries a per-subject override. */
  isOverridden: boolean;
  /** Preferred models, as **server names** — `forecast_model_pins`. */
  pins: string[] | null;
  /** Server names actually served at this subject. */
  available: string[];
  /** `Forecast.hasForecastPreference(pins, mode, strategy)`. */
  hasPreference: boolean;
  /**
   * `selectIsPremium` — the same predicate `resolveSelectedForecastModel` gets.
   *
   * Load-bearing, not decorative: without it a free account with
   * `pins: ['AROME']` at a French station reads "GFS — your first preferred
   * model that reaches this station", because the pin *is* available even
   * though GFS is what gets served. The settings screen states the opposite
   * ("Free accounts are served GFS everywhere"); this keeps the two agreeing.
   */
  isPremium: boolean;
};

export const deriveForecastSelection = ({
  resolved,
  preferred,
  isOverridden,
  pins,
  available,
  hasPreference,
  isPremium,
}: Input): ForecastSelectionState => {
  // First, because it overrules everything saved. A lapsed subscriber still
  // has their override in the store — it takes effect again on resubscribe —
  // but it is not what is being served now, so the sheet must not offer to
  // "reset" something that is not in force.
  if (!isPremium) {
    return {kind: 'freeTier', model: resolved, preferred};
  }
  if (isOverridden) {
    return {kind: 'overridden', model: resolved, preferred};
  }
  // Mirrors the server's resolve_preference and computePinCoverage: the pins
  // are walked in order and the first one served here wins.
  //
  // The second half of the condition is not redundant. `resolved` comes from
  // `forecastModelOrder`, which the backend re-derives from the pins, so a
  // local pin edit is visible here a round-trip before it reaches the order —
  // and offline, indefinitely. Claiming a pin won while a different model is
  // being served is the failure mode; falling back to "everywhere else
  // decided" for that window is merely conservative.
  const pin = (pins ?? []).find((name) => available.includes(name));
  if (pin && resolved?.serverId.forecastModel === pin) {
    return {kind: 'pinWins', model: resolved, preferred, pin};
  }
  return {
    kind: hasPreference ? 'fallback' : 'noPreference',
    model: resolved,
    preferred,
  };
};

/**
 * The sentence under the picker's state strip.
 *
 * Mirrors `coverageMessage` in `utils/Forecast/preference.ts` — descriptor plus
 * values, formatted by the caller — so both message maps in this area have the
 * same shape.
 *
 * `values` is omitted rather than empty for the states that name no model: an
 * unused placeholder is a translation that 25 locales have to be told to ignore.
 */
export const forecastSelectionMessage = (
  state: ForecastSelectionState,
  subject: ForecastSubjectKind,
  /** Prints the model's name, which may itself be a message. */
  formatMessage: (descriptor: MessageDescriptor) => string,
): {descriptor: MessageDescriptor; values?: Record<string, string>} => {
  const isStation = subject === 'station';
  switch (state.kind) {
    case 'freeTier':
      // The settings screen's own free banner, verbatim: one sentence for the
      // whole feature rather than a second way of saying it.
      return {descriptor: messages.premium.freeBannerBody};
    case 'overridden':
      return {descriptor: messages.forecastModels.forecastOverrideElsewhere};
    case 'pinWins':
      return {
        descriptor: isStation
          ? messages.forecastModels.forecastPinWinsStation
          : messages.forecastModels.forecastPinWinsLocation,
        values: {
          model: state.model
            ? formatForecastModelTitle(state.model.title, formatMessage)
            : '',
        },
      };
    case 'fallback':
      return {
        descriptor: isStation
          ? messages.forecastModels.forecastNoPinStation
          : messages.forecastModels.forecastNoPinLocation,
      };
    case 'noPreference':
      // No placeholder: the strip states the profile is pristine, and the
      // "use my preferred models" row below it names what that resolves to.
      // Saying the model in both put the same sentence on screen twice.
      return {descriptor: messages.forecastModels.forecastNoPreferredModels};
  }
};

/** The state strip's heading — a two-way branch, kept beside its detail line. */
export const forecastSelectionTitle = (
  state: ForecastSelectionState,
  subject: ForecastSubjectKind,
): MessageDescriptor => {
  if (state.kind === 'freeTier') {
    return messages.premium.freeBannerTitle;
  }
  if (state.kind !== 'overridden') {
    return messages.forecastModels.forecastFollowingPreferences;
  }
  return subject === 'station'
    ? messages.forecastModels.forecastSetForStation
    : messages.forecastModels.forecastSetForLocation;
};
